import React, { useState, useEffect, useCallback } from 'react'
import { Filter, RotateCcw, Key, Save } from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import roleService from '../../services/roleService'
import menuItemService from '../../services/menuItemService'
import submoduleService from '../../services/submoduleService'

const PERMISSION_KEYS = [
  { key: 'create', label: 'C' },
  { key: 'view', label: 'V' },
  { key: 'edit', label: 'E' },
  { key: 'approve', label: 'A' },
  { key: 'download', label: 'D' },
  { key: 'generate_report', label: 'G' }
]

const PERMISSIONS_BY_TYPE = {
  Master: ['create', 'view', 'edit', 'approve', 'download'],
  Transaction: ['create', 'view', 'edit', 'approve', 'download', 'generate_report'],
  Reports: ['view', 'download']
}

const ModulePermissions = () => {
  const navigate = useNavigate()
  const [roles, setRoles] = useState([])
  const [menuItems, setMenuItems] = useState([])
  const [modules, setModules] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [filter, setFilter] = useState({ roleId: '', moduleName: '' })
  const [appliedFilter, setAppliedFilter] = useState({ roleId: '', moduleName: '' })
  const [rolePermissions, setRolePermissions] = useState({})
  const [permissionGrid, setPermissionGrid] = useState({})

  const loadRoles = useCallback(async () => {
    try {
      const res = await roleService.getAllRoles()
      if (res?.success && Array.isArray(res.data)) {
        setRoles(res.data)
      }
    } catch (err) {
      console.error(err)
      toast.error('Failed to load roles')
    }
  }, [])

  const loadMenuItems = useCallback(async () => {
    try {
      const res = await menuItemService.getAllMenuItems({ includeInactive: true })
      if (res?.success && Array.isArray(res.data)) {
        setMenuItems(res.data)
      }
    } catch (err) {
      console.error(err)
      toast.error('Failed to load business functions')
    }
  }, [])

  const loadModules = useCallback(async () => {
    if (!appliedFilter.moduleName) {
      setModules([])
      return
    }
    try {
      const res = await submoduleService.getAllSubmodules({
        includeInactive: true,
        moduleName: appliedFilter.moduleName
      })
      if (res?.success && Array.isArray(res.data)) {
        setModules(res.data)
      } else {
        setModules([])
      }
    } catch (err) {
      console.error(err)
      setModules([])
    }
  }, [appliedFilter.moduleName])

  const loadRoleById = useCallback(async (roleId) => {
    if (!roleId) return
    try {
      const res = await roleService.getRoleById(roleId)
      if (res?.success && res.data) {
        const perms = (res.data.permissions || []).reduce((acc, p) => {
          if (p.module && Array.isArray(p.actions)) {
            acc[p.module] = [...(p.actions || [])]
          }
          return acc
        }, {})
        setRolePermissions(perms)
        return perms
      }
    } catch (err) {
      console.error(err)
      toast.error('Failed to load role permissions')
      setRolePermissions({})
    }
    return {}
  }, [])

  useEffect(() => {
    loadRoles()
    loadMenuItems()
  }, [loadRoles, loadMenuItems])

  useEffect(() => {
    loadModules()
  }, [loadModules])

  useEffect(() => {
    if (appliedFilter.roleId && appliedFilter.moduleName && modules.length > 0) {
      setLoading(true)
      loadRoleById(appliedFilter.roleId).then((perms) => {
        const grid = {}
        modules.forEach((mod) => {
          const code = mod.submoduleCode || mod._id
          const actions = perms[code] || []
          const allowedForType = PERMISSIONS_BY_TYPE[mod.type] || PERMISSIONS_BY_TYPE.Master
          grid[code] = {}
          allowedForType.forEach((a) => {
            grid[code][a] = actions.includes(a)
          })
        })
        setPermissionGrid(grid)
        setLoading(false)
      })
    } else {
      setPermissionGrid({})
      setLoading(false)
    }
  }, [appliedFilter.roleId, appliedFilter.moduleName, modules, loadRoleById])

  const handleApplyFilter = () => {
    if (!filter.roleId || !filter.moduleName) {
      toast.warn('Please select Role and Business Function')
      return
    }
    setAppliedFilter({ ...filter })
  }

  const handleResetFilter = () => {
    setFilter({ roleId: '', moduleName: '' })
    setAppliedFilter({ roleId: '', moduleName: '' })
    setPermissionGrid({})
    setRolePermissions({})
  }

  const togglePermission = (moduleCode, action) => {
    setPermissionGrid((prev) => ({
      ...prev,
      [moduleCode]: {
        ...(prev[moduleCode] || {}),
        [action]: !prev[moduleCode]?.[action]
      }
    }))
  }

  const toggleSelectAll = (type) => {
    const modsOfType = modules.filter((m) => m.type === type)
    if (modsOfType.length === 0) return
    const allowed = PERMISSIONS_BY_TYPE[type] || PERMISSIONS_BY_TYPE.Master
    const allChecked = modsOfType.every((m) => {
      const code = m.submoduleCode || m._id
      return allowed.every((a) => permissionGrid[code]?.[a])
    })
    setPermissionGrid((prev) => {
      const next = { ...prev }
      modsOfType.forEach((m) => {
        const code = m.submoduleCode || m._id
        next[code] = { ...(next[code] || {}) }
        allowed.forEach((a) => {
          next[code][a] = !allChecked
        })
      })
      return next
    })
  }

  const handleSave = async () => {
    if (!appliedFilter.roleId) {
      toast.error('Select a role first')
      return
    }
    try {
      setSaving(true)
      const roleRes = await roleService.getRoleById(appliedFilter.roleId)
      if (!roleRes?.success || !roleRes.data) {
        toast.error('Role not found')
        return
      }
      const currentModuleCodes = new Set(modules.map((m) => m.submoduleCode || m._id))
      const existingPerms = (roleRes.data.permissions || []).filter(
        (p) => p.module && !currentModuleCodes.has(p.module)
      )
      const newPerms = modules.map((m) => {
        const code = m.submoduleCode || m._id
        const allowed = PERMISSIONS_BY_TYPE[m.type] || []
        const checked = allowed.filter((a) => permissionGrid[code]?.[a])
        return { module: code, actions: checked }
      }).filter((p) => p.actions.length > 0)
      const allPerms = [...existingPerms, ...newPerms]
      const permissionStrings = allPerms.flatMap((p) => p.actions.map((a) => `${p.module}_${a}`))
      const displayData = roleService.transformRoleForDisplay(roleRes.data)
      const payload = roleService.transformRoleForSubmission({
        ...displayData,
        permissions: permissionStrings
      })
      const updateRes = await roleService.updateRole(appliedFilter.roleId, payload)
      if (updateRes?.success) {
        toast.success('Permissions saved successfully')
        loadRoleById(appliedFilter.roleId).then((perms) => {
          const grid = {}
          modules.forEach((mod) => {
            const code = mod.submoduleCode || mod._id
            const actions = perms[code] || []
            const allowed = PERMISSIONS_BY_TYPE[mod.type] || []
            grid[code] = {}
            allowed.forEach((a) => {
              grid[code][a] = actions.includes(a)
            })
          })
          setPermissionGrid(grid)
        })
      } else {
        toast.error(updateRes?.message || 'Failed to save')
      }
    } catch (err) {
      console.error(err)
      toast.error(err?.message || 'Failed to save permissions')
    } finally {
      setSaving(false)
    }
  }

  const breadcrumbItems = [
    { label: 'Settings', path: '/settings', onClick: () => navigate('/settings') },
    { label: 'Masters', path: '/settings', onClick: () => navigate('/settings') },
    { label: 'Security Master', path: '/settings/security-master', onClick: () => navigate('/settings/security-master') },
    { label: 'Module Permissions', path: '/settings/module-permissions', isCurrent: true }
  ]

  const groupedByType = {
    Master: modules.filter((m) => m.type === 'Master'),
    Transaction: modules.filter((m) => m.type === 'Transaction'),
    Reports: modules.filter((m) => m.type === 'Reports')
  }

  const renderPermissionTable = (type) => {
    const rows = groupedByType[type] || []
    const allowed = PERMISSIONS_BY_TYPE[type] || PERMISSIONS_BY_TYPE.Master
    if (rows.length === 0) {
      return (
        <div className="py-3 text-muted text-center">No modules for this type</div>
      )
    }
    const allChecked = rows.every((m) => {
      const code = m.submoduleCode || m._id
      return allowed.every((a) => permissionGrid[code]?.[a])
    })
    return (
      <div className="table-responsive">
        <table className="table table-sm table-bordered">
          <thead>
            <tr>
              <th style={{ minWidth: 200 }}>Module Name</th>
              {allowed.map((a) => {
                const pk = PERMISSION_KEYS.find((p) => p.key === a)
                return <th key={a} className="text-center" style={{ width: 44 }}>{pk?.label || a}</th>
              })}
            </tr>
            <tr>
              <td>
                <label className="d-flex align-items-center gap-2 mb-0">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    onChange={() => toggleSelectAll(type)}
                  />
                  <span>Select All</span>
                </label>
              </td>
              {allowed.map((a) => (
                <td key={a} className="text-center">
                  <input
                    type="checkbox"
                    checked={rows.every((m) => permissionGrid[m.submoduleCode || m._id]?.[a])}
                    onChange={() => {
                      const newVal = !rows.every((m) => permissionGrid[m.submoduleCode || m._id]?.[a])
                      setPermissionGrid((prev) => {
                        const next = { ...prev }
                        rows.forEach((m) => {
                          const code = m.submoduleCode || m._id
                          next[code] = { ...(next[code] || {}), [a]: newVal }
                        })
                        return next
                      })
                    }}
                  />
                </td>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => {
              const code = m.submoduleCode || m._id
              const displayName = m.displayName || m.name || '-'
              return (
                <tr key={code}>
                  <td>{displayName}</td>
                  {allowed.map((a) => (
                    <td key={a} className="text-center">
                      <input
                        type="checkbox"
                        checked={!!permissionGrid[code]?.[a]}
                        onChange={() => togglePermission(code, a)}
                      />
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="page-header">
        <div className="page-title">
          <h1>Module Permissions</h1>
          <p>Assign granular permissions (Create, View, Edit, Download, Approve, Generate Report) to modules by role</p>
        </div>
      </div>

      {/* Filter section */}
      <div className="form-container" style={{ marginBottom: '1.5rem' }}>
        <div className="sac-form-header">
          <Key size={20} className="me-2" />
          <h2 className="mb-0">Module Permissions</h2>
        </div>
        <div className="form-content" style={{ padding: '16px 20px' }}>
          <div className="row g-3 align-items-end">
            <div className="col-md-4">
              <label className="form-label required">Role</label>
              <select
                className="form-select"
                value={filter.roleId}
                onChange={(e) => setFilter((f) => ({ ...f, roleId: e.target.value }))}
              >
                <option value="">Select Role</option>
                {roles.map((r) => (
                  <option key={r._id} value={r._id}>{r.roleName || r.displayRoleName}</option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label required">Business Function / Module</label>
              <select
                className="form-select"
                value={filter.moduleName}
                onChange={(e) => setFilter((f) => ({ ...f, moduleName: e.target.value }))}
              >
                <option value="">Select Business Function</option>
                {menuItems.map((m) => (
                  <option key={m._id} value={m._id}>{m.label || m.menuCode}</option>
                ))}
              </select>
            </div>
            <div className="col-md-4 d-flex gap-2">
              <button type="button" className="btn btn-primary" onClick={handleApplyFilter}>
                <Filter size={16} /> Apply Filter
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={handleResetFilter}>
                <RotateCcw size={16} /> Reset Filter
              </button>
            </div>
          </div>
          <p className="text-muted small mt-2 mb-0">
            Note: C - Create, V - View, E - Edit, D - Download, A - Approve, G - Generate Report
          </p>
        </div>
      </div>

      {appliedFilter.roleId && appliedFilter.moduleName && (
        <>
          <div className="d-flex justify-content-end mb-3">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving || loading}
            >
              <Save size={16} /> {saving ? 'Saving...' : 'Save Permissions'}
            </button>
          </div>

          {loading ? (
            <div className="text-center py-5">Loading...</div>
          ) : (
            <div className="row g-4">
              <div className="col-12">
                <div className="form-container">
                  <div className="sac-form-header bg-light">
                    <h3 className="mb-0">Masters</h3>
                  </div>
                  <div className="form-content p-3">
                    {renderPermissionTable('Master')}
                  </div>
                </div>
              </div>
              <div className="col-12">
                <div className="form-container">
                  <div className="sac-form-header bg-light">
                    <h3 className="mb-0">Transactions</h3>
                  </div>
                  <div className="form-content p-3">
                    {renderPermissionTable('Transaction')}
                  </div>
                </div>
              </div>
              <div className="col-12">
                <div className="form-container">
                  <div className="sac-form-header bg-light">
                    <h3 className="mb-0">Reports</h3>
                  </div>
                  <div className="form-content p-3">
                    {renderPermissionTable('Reports')}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {(!appliedFilter.roleId || !appliedFilter.moduleName) && (
        <div className="form-container">
          <div className="form-content text-center py-5 text-muted">
            Select Role and Business Function, then click Apply Filter to manage permissions.
          </div>
        </div>
      )}
    </div>
  )
}

export default ModulePermissions
